import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link as LinkIcon, FileText, Upload, X as XIcon, Loader2, Plus } from "lucide-react";
import { sourcesApi } from "@/lib/apiClient";

// Build the grounding context string the backend receives from a set of sources.
export function assembleSourcesContext(sources = []) {
  if (!sources.length) return "";
  return sources
    .map((s, i) => `Source ${i + 1} — ${s.title}${s.url ? ` (${s.url})` : ""}\n${s.text}`)
    .join("\n\n");
}

/**
 * Add multiple grounding sources to a run: paste URLs (fetched + extracted) or
 * upload documents (PDF / Word / text, extracted server-side). Controlled:
 * owns nothing but the in-progress URL field; the list lives in `sources`.
 */
export function SourcesPicker({ sources = [], onChange, variant = "light", max = 8 }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const dark = variant === "dark";
  const atMax = sources.length >= max;

  const add = (src) =>
    onChange([...sources, { id: `src_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, ...src }]);
  const remove = (id) => onChange(sources.filter((s) => s.id !== id));

  const addUrl = async () => {
    const link = url.trim();
    if (!link || busy || atMax) return;
    setBusy(true);
    setError(null);
    try {
      const r = await sourcesApi.extract(link);
      add({ kind: "url", title: r.title || link, url: r.url || link, text: r.text, words: r.word_count, truncated: r.truncated });
      setUrl("");
    } catch (e) {
      setError(e.message || "Couldn't read that URL");
    } finally {
      setBusy(false);
    }
  };

  const addFile = async (file) => {
    if (!file || busy || atMax) return;
    setBusy(true);
    setError(null);
    try {
      const r = await sourcesApi.extractFile(file);
      add({ kind: "file", title: r.title || file.name, url: "", text: r.text, words: r.word_count, truncated: r.truncated });
    } catch (e) {
      setError(e.message || "Couldn't read that file");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const inputCls = dark
    ? "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
    : "border-[color:var(--border-subtle)] bg-card text-foreground placeholder:text-muted-foreground";
  const chipCls = dark
    ? "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-foreground"
    : "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-2)]";
  const muted = dark ? "text-muted-foreground" : "text-muted-foreground";

  return (
    <div className="space-y-2" data-testid="sources-picker">
      <div className="flex flex-wrap gap-2">
        <Input
          className={`min-w-[180px] flex-1 ${inputCls}`}
          value={url}
          onChange={(e) => { setUrl(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
          placeholder="Paste a source URL…"
          disabled={atMax}
          data-testid="sources-url-input"
        />
        <Button type="button" variant="outline" onClick={addUrl} disabled={busy || atMax || !url.trim()} className="shrink-0 gap-1.5" data-testid="sources-add-url">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
        </Button>
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy || atMax} className="shrink-0 gap-1.5" data-testid="sources-add-file">
          <Upload className="h-3.5 w-3.5" /> Upload
        </Button>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md,.markdown" className="hidden" onChange={(e) => addFile(e.target.files?.[0])} data-testid="sources-file-input" />
      </div>
      {error && <p className="text-[11px] text-rose-500" data-testid="sources-error">{error}</p>}
      {sources.length > 0 && (
        <ul className="space-y-1.5" data-testid="sources-list">
          {sources.map((s) => (
            <li key={s.id} className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 ${chipCls}`}>
              <span className="flex min-w-0 items-center gap-1.5">
                {s.kind === "file"
                  ? <FileText className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  : <LinkIcon className="h-3.5 w-3.5 shrink-0 text-[color:var(--accent-cyan)]" />}
                <span className="truncate text-[12px] font-medium">{s.title}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className={`text-[10.5px] ${muted}`}>{(s.words || 0).toLocaleString()} words{s.truncated ? " · trimmed" : ""}</span>
                <button type="button" onClick={() => remove(s.id)} className={`${muted} hover:text-rose-500`} aria-label="Remove source"><XIcon className="h-3.5 w-3.5" /></button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className={`text-[10.5px] ${muted}`}>
        {atMax ? `Max ${max} sources reached.` : "Add URLs or upload PDF, Word, or text files — the agent grounds on all of them."}
      </p>
    </div>
  );
}
